import * as React from 'react';
import { Modal, Button, Grid, XFilledIcon} from 'react-unity';
import { Alert } from '../../../common';
import '../../../../components/PortOpeningRequests/components/porRule/RuleModal.css';
import TextAreaField from '../../../common/form-controls/TextareaField';
import {ToggleField} from '../../../common/';
import TextInputField from '../../../common/form-controls/TextInputField';
import { AbstractRulesForm, IRuleStateForm, RulesFormProps } from './OnPremAbstractRulesForm';
import { RuleAction } from '../../../../models/enums/POR/RuleAction';
import SelectField from '../../../common/form-controls/SelectField';
import { RuleOperation } from '../../../../models/enums/POR/RuleOperation';
import { PortOpeningRequestsDirection } from '../../../../models/enums/POR/PortOpeningRequestsDirection';
import ObjectLookup from '../../../common/form-controls/Lookups/ObjectLookup';
import OnPremPortOpeningObject from '../../../../models/entities/OnPremPortOpeningObject';
interface RuleModalInternetDMZProps extends RulesFormProps {
	direction: PortOpeningRequestsDirection;
}

interface SubToOnPremRuleStateForm extends IRuleStateForm {
	name: string;
	sourceObjects: string;
	sourceIPs: string;
	destinationURLs: string;
	protocol: string;
	sourceObjectsList: OnPremPortOpeningObject[];
	destinationObjectsList: OnPremPortOpeningObject[];
}
interface RuleModalInternetDMZState {
	stateForm: SubToOnPremRuleStateForm;
}

export default class RuleModalInternetDMZ extends AbstractRulesForm<
	RuleModalInternetDMZProps,
	RuleModalInternetDMZState
> {
	constructor(props: RuleModalInternetDMZProps) {
		super(props);
		this.state = {
			stateForm: this.props.isFromHistory ? this.getRuleCopy() : this.getDefaultForm(),
		};

		this.initFormModel();
	}

	isURLValid = (url) => /^(https?:\/\/)?[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)+(\/[^\s:]*)?$/.test(url);

	initFormModel() {
		super.initFormModel();

		this.formModel.fields.sourceIPs.validation.rules.push(
			this.IpInputValidationRule({
				onlyPrivateAddresses: false,
			})
		);

		this.formModel.fields.destinationIPs.validation.rules.push(
			this.IpInputValidationRule({
				onlyPrivateAddresses: false,
			})
		);

		this.formModel.fields.sourceIPs.validation.required = () =>
			!(this.state.stateForm.sourceObjects?.trim().length > 0);

		this.formModel.fields.sourceObjects.validation.required = () =>
			!(this.state.stateForm.sourceIPs?.trim().length > 0);

		this.formModel.fields.destinationIPs.validation.required = () =>
			!(this.state.stateForm.destinationURLs?.trim().length > 0) &&
			!(this.state.stateForm.destinationObjects?.trim().length > 0);

		this.formModel.fields.destinationObjects.validation.required = () =>
			!(this.state.stateForm.destinationURLs?.trim().length > 0) &&
			!(this.state.stateForm.destinationIPs?.trim().length > 0);

		this.formModel.addField('protocol', {
			getValue: () => this.state.stateForm.protocol,
			validation: {
				required: true,
			},
		});

		this.formModel.fields.destinationPorts.validation.rules.push(
			this.PortInputValidationRule({
				minimumPort: 0,
			})
		);

		const numberOfDestinationItemsValidation = {
			assert: () => {
				const numberOfIPs = this.state.stateForm.destinationIPs?.split(';').filter((s) => s !== '').length ?? 0;
				const numberOfURLs =
					this.state.stateForm.destinationURLs?.split(';').filter((s) => s !== '').length ?? 0;
				const numberOfDestinationObjects = this.state.stateForm.destinationObjects?.trim().length > 0 ? 1 : 0;
				const numberOfDestinationItems = numberOfIPs + numberOfURLs + numberOfDestinationObjects;

				const maxNumberOfDestinationItems = 500;
				return numberOfDestinationItems <= maxNumberOfDestinationItems;
			},
			message: "Total Destination URLs and IPs can't be greater than 500",
		};

		this.formModel.addField('destinationURLs', {
			getValue: () => this.parseMultipleValues(this.state.stateForm.destinationURLs),
			validation: {
				required: () =>
					!(this.state.stateForm.destinationIPs?.trim().length > 0) &&
					!(this.state.stateForm.destinationObjects?.trim().length > 0),
				rules: [
					{
						assert: () => !this.hasEmptyValues(this.state.stateForm.destinationURLs),
						message: 'Empty values are not allowed.',
					},
					{
						assert: () =>
							!(
								this.state.stateForm.destinationURLs?.includes('https://') ||
								this.state.stateForm.destinationURLs?.includes('http://')
							),
						message: 'URL cannot have "http" or "https"',
					},
					{
						assert: (value) => {
							const urls = this.parseMultipleValues(value)?.split(';');
							return urls?.every((url) => this.isURLValid(url));
						},
						message: (value) => {
							if (value !== undefined) {
								const invalidAddress = this.parseMultipleValues(value)
									?.split(';')
									.find((url) => !this.isURLValid(url));
								return `${invalidAddress} is an invalid URL.`;
							} else return 'Empty values are not allowed.';
						},
					},
					numberOfDestinationItemsValidation,
				],
			},
		});
	}

	// eslint-disable-next-line class-methods-use-this
	getDefaultFormState() {
		return {
			operation: RuleOperation.Add,
			action: RuleAction.Allow,
			sslBypass: 'false',
			name: '',
			protocol: '',
			sourceObjectsList: [],
            destinationObjectsList: [],
		};
	}

	parseObjectsList(value: string): OnPremPortOpeningObject[] {
			return value
				? value.split(',').filter(n => n.trim()).map((name, idx) => ({
					id: -(idx + 1),
					name: name.trim(),
				} as OnPremPortOpeningObject))
				: [];
		}
	
		getRuleCopy() {
			const copy = super.getRuleCopy() as unknown as SubToOnPremRuleStateForm;
			return {
				...copy,
				sourceObjectsList: this.parseObjectsList(copy.sourceObjects),
				destinationObjectsList: this.parseObjectsList(copy.destinationObjects),
			} as unknown as ReturnType<AbstractRulesForm<RuleModalInternetDMZProps, RuleModalInternetDMZState>['getRuleCopy']>;
		}
	
		handleObjectSelection = (
			field: 'sourceObjects' | 'destinationObjects',
			listField: 'sourceObjectsList' | 'destinationObjectsList',
			obj: OnPremPortOpeningObject | null
		) => {
			if (!obj) return;
			const list = this.state.stateForm[listField] ?? [];
			if (list.find(o => o.name === obj.name)) return;
	
			const updatedList = [...list, obj];
			this.handleStateFormChange(
				[field, listField],
				[updatedList.map(o => o.name).join(','), updatedList]
			);
		};
	
		handleRemoveObject = (
			field: 'sourceObjects' | 'destinationObjects',
			listField: 'sourceObjectsList' | 'destinationObjectsList',
			objName: string
		) => {
			const updatedList = (this.state.stateForm[listField] ?? []).filter(o => o.name !== objName);
			this.handleStateFormChange(
				[field, listField],
				[updatedList.map(o => o.name).join(','), updatedList]
			);
		};

	resetIPsValidations() {
		this.formModel.fields.sourceIPs.validation.rules = [];
		this.formModel.fields.destinationIPs.validation.rules = [];
	}

	wasEditted() {
		if (this.props.editingRule) {
			return (
				this.props.editingRule.description != this.stateFormHandler().description.value ||
				this.props.editingRule.action.value != this.stateFormHandler().action.value ||
				this.props.editingRule.sourceIPs != this.state.stateForm.sourceIPs ||
				this.props.editingRule.destinationIPs != this.stateFormHandler().destinationIPs.value ||
				this.props.editingRule.destinationPorts != this.stateFormHandler().destinationPorts.value ||
				this.props.editingRule.destinationURLs != this.state.stateForm.destinationURLs ||
				this.props.editingRule.protocol != this.state.stateForm.protocol ||
				this.props.editingRule.operation != this.state.stateForm.operation ||
				this.props.editingRule.sourceObjects != this.state.stateForm.sourceObjects ||
				this.props.editingRule.destinationObjects != this.state.stateForm.destinationObjects
			);
		}
		return true;
	}

	renderObjectsLookup(
	label: string,
	field: 'sourceObjects' | 'destinationObjects',
	listField: 'sourceObjectsList' | 'destinationObjectsList'
    ) {
        const list = this.state.stateForm[listField] ?? [];
        return (
            <div>
                <label className="em-c-field__label">{label}</label>
                {this.formCanBeEdited() && (
                    <ObjectLookup
                        label=""
                        placeholder="Search for objects by name..."
                        value={null}
                        onChange={(obj) => this.handleObjectSelection(field, listField, obj)}
                        note="For pre-existing firewall objects only. Type at least 3 characters to search."
                    />
                )}
                {list.length > 0 && (
                    <div style={{ marginTop: '8px' }}>
                        {list.map((obj) => (
                            <div
                                key={obj.name}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    padding: '4px 8px',
                                    margin: '4px',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px',
                                    backgroundColor: '#f5f5f5',
                                }}
                            >
                                <span style={{ marginRight: '8px' }}>{obj.name}</span>
                                {this.formCanBeEdited() && (
                                    <button
                                        type="button"
                                        onClick={() => this.handleRemoveObject(field, listField, obj.name)}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            color: '#cc0000',
                                            fontWeight: 'bold',
                                            fontSize: '14px',
                                        }}
                                    >
                                        ✕
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    }

	render() {
		return (
			<Modal show={this.props.visible} onHide={() => {}}>
				<Modal.Window className="ruleModal">
					<Modal.Window.Header>
						<Modal.Window.Header.Title>
							{this.props.isReadOnly ? 'View Rule' : !this.isModalForNewRule() ? 'Edit Rule' : 'New Rule'}
						</Modal.Window.Header.Title>
						<Modal.Window.Header.CloseButton onClick={this.props.onClose}>
							<XFilledIcon size="small" />
						</Modal.Window.Header.CloseButton>
					</Modal.Window.Header>
					<Modal.Window.Body>
						<ToggleField
							{...this.stateFormHandler().operation}
							id='operation'
							label="Operation"
							disabled={!this.formCanBeEdited()}
							value={this.state.stateForm.operation?.value}
							options={
								this.props.isReadOnly
									? [new RuleOperation().fromValue(this.stateFormHandler().operation.value)]
									: this.isModalForNewRule()
										? [RuleOperation.Add]
										: [RuleOperation.Modify, RuleOperation.Remove]
							}
						/>
						<Grid variant="4-up">
							<Grid.Item>
								<ToggleField
									{...this.stateFormHandler().isTemporary}
									value={this.state.stateForm.isTemporary?.toString()}
									options={
										this.formCanBeEdited()
											? [
													{ text: 'Temporary', value: 'true' },
													{ text: 'Permanent', value: 'false' },
												]
											: this.state.stateForm.isTemporary
												? [{ text: 'Temporary', value: 'true' }]
												: [{ text: 'Permanent', value: 'false' }]
									}
									id='type'
									label="Type"
									disabled={!this.formCanBeEdited()}
								/>
							</Grid.Item>
						</Grid>
						<Grid variant="2-up">
							<Grid.Item>
								<TextInputField
									label="Rule Name"
									value={this.state.stateForm.name}
									validation={this.formModel.fields.name.validation}
									onChange={(event) => {
										this.handleStateFormChange('name', event.target.value);
									}}
									disabled={
										(this.state.stateForm.operation?.name !== RuleOperation.Add.name &&
											this.state.stateForm.operation?.name !== RuleOperation.Remove.name) ||
										!this.formCanBeEdited()
									}
								/>
							</Grid.Item>
						</Grid>
						<>
							<Grid variant="2-up">
								<Grid.Item>
									<ToggleField
										{...this.stateFormHandler().action}
										options={
											(this.formCanBeEdited() || this.stateFormHandler().action?.value === undefined)
												? [RuleAction.Allow, RuleAction.Block]
												: [new RuleAction().fromValue(this.stateFormHandler().action.value)]}
										id='action'
										label="Action"
										disabled={!this.formCanBeEdited()}
									/>
								</Grid.Item>
							</Grid>
							<Grid variant="2-up">
								<Grid.Item>
									{this.renderObjectsLookup('Source Objects/Host Name', 'sourceObjects', 'sourceObjectsList')}
								</Grid.Item>
								<Grid.Item>
									<TextAreaField
										value={this.state.stateForm.sourceIPs}
										label="Source (IPs / IP Range / CIDR)"
										validation={this.formModel.fields.sourceIPs.validation}
										onChange={(event) => {
											let sanitizedValue = event.target.value.replace(/\n/g, '');
											this.handleStateFormChange('sourceIPs', sanitizedValue);
										}}
										disabled={
											!this.formCanBeEdited() ||
											this.props.direction == PortOpeningRequestsDirection.iBossProxyToInternet
										}
									/>
									{this.assertSourceIPSContainsLargeSubnet() ? (
										<Alert
											variant="warning"
											body={this.getlargeSubnetAlerttext()}>
										</Alert>
									) : (
										<></>
									)}
								</Grid.Item>
							</Grid>
							<Grid variant="2-up">
								<Grid.Item>
									<TextAreaField
										{...this.stateFormHandler().destinationIPs}
										label="Destination (IPs / IP Range / CIDR)"
										disabled={!this.formCanBeEdited()}
										onChange={(event) => {
											let sanitizedValue = event.target.value.replace(/\n/g, '');
											this.handleStateFormChange('destinationIPs', sanitizedValue);
										}}
									/>
									{this.assertDestinationIPSContainsLargeSubnet() ? (
										<Alert
											variant="warning"
											body={this.getlargeSubnetAlerttext()}>
										</Alert>
									) : (
										<></>
									)}
								</Grid.Item>
								<Grid.Item>
									<TextAreaField
										label="Destination (URLs)"
										value={this.state.stateForm.destinationURLs}
										validation={this.formModel.fields.destinationURLs.validation}
										onChange={(event) => {
											this.handleStateFormChange('destinationURLs', event.target.value);
										}}
										note="Multiple values are allowed, separated by semicolons."
										disabled={!this.formCanBeEdited()}
									/>
								</Grid.Item>
							</Grid>
							<Grid variant="2-up">
								<Grid.Item>
									{this.renderObjectsLookup('Destination Objects/Host Name', 'destinationObjects', 'destinationObjectsList')}
								</Grid.Item>
								<Grid.Item>
									<TextAreaField
										{...this.stateFormHandler().destinationPorts}
										label="Destination Ports"
										disabled={!this.formCanBeEdited()}
									/>
								</Grid.Item>
							</Grid>
							<Grid variant="2-up">
								<Grid.Item>
									<TextAreaField
										{...this.stateFormHandler().description}
										label="Description"
										disabled={!this.formCanBeEdited()}
									/>
								</Grid.Item>
								<Grid.Item>
									<SelectField
										label="Protocol"
										value={this.state.stateForm.protocol}
										options={['TCP', 'UDP', 'ICMP echo-request', 'ICMP echo-reply']}
										onChange={(event) => this.handleStateFormChange('protocol', event.target.value)}
										validation={this.formModel.fields.protocol.validation}
										disabled={!this.formCanBeEdited()}
									/>
								</Grid.Item>
							</Grid>
						</>
					</Modal.Window.Body>
					{!this.props.isReadOnly && (
						<Modal.Window.Footer>
							<Grid variant="2-up">
								<Grid.Item>
									<Button
										variant="primary"
										disabled={!this.formModel.isValid() || !this.wasEditted()}
										onClick={this.handleRequest}
									>
										{this.props.editingRule ? 'Confirm' : 'Create'}
									</Button>
								</Grid.Item>
								<Grid.Item>
									<Button variant="secondary" onClick={this.handleCancel}>
										Cancel
									</Button>
								</Grid.Item>
							</Grid>
						</Modal.Window.Footer>
					)}
				</Modal.Window>
			</Modal>
		);
	}
}
